import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { formatINR } from "@/api";

// Premium cinematic category banner.
// Design intent:
//  • Full-bleed on desktop with generous vertical space (feels editorial, not "adslot").
//  • Ken Burns zoom on active slide (subtle motion, no dizzying pan).
//  • Overlay chrome (kicker / title / copy / CTA) renders ONLY when the admin
//    supplies that field. So image-only banners (like the PING G440 asset that
//    already has typography baked in) show CLEAN — no double text, no button
//    stacked on button. Set overlay_mode="image_only" from admin to force this.
//  • Slide indicators are a row of thin bars that "fill" on active, à la
//    high-end fashion e-com (Zegna, Hermès Sport).
export const CategoryBanner = ({ categoryName, bgImage, shopHref, customSlides, featuredProducts = [], featuredBase = "/equipment" }) => {
  const defaultSlides = [
    {
      kicker: "New Season",
      title: categoryName,
      copy: "Engineered to perform. Explore the range and find your fit.",
      cta: "Shop Now",
      href: shopHref,
      image: bgImage,
      overlay_mode: "text",
    },
    {
      kicker: "Custom Fit · Custom Built",
      title: "Dialed In For You",
      copy: `Book a fitting and build your perfect ${categoryName.toLowerCase()}.`,
      cta: "Book A Fitting",
      href: "/fitting",
      image: bgImage,
      overlay_mode: "text",
    },
    {
      kicker: "J&B Golf · PING India",
      title: "Genuine. Delivered.",
      copy: "100% authentic PING, shipped across India with expert support.",
      cta: "Shop Now",
      href: shopHref,
      image: bgImage,
      overlay_mode: "text",
    },
  ];

  const slides = (customSlides && customSlides.length)
    ? customSlides.map((s) => ({
        kicker: s.kicker || "",
        title: s.title || "",
        copy: s.copy || "",
        cta: s.cta_label || "",
        href: s.cta_href || shopHref,
        image: s.image || bgImage,
        image_fit: s.image_fit || "cover",
        image_position: s.image_position || "center",
        // Backward-compat: if admin didn't set overlay_mode explicitly,
        // treat slides without a `title` as image-only (safer default —
        // uploaded banners often have their own text baked into the JPG).
        overlay_mode: s.overlay_mode || (s.title ? "text" : "image_only"),
      }))
    : defaultSlides.map((s) => ({ ...s, image_fit: "cover", image_position: "center" }));

  const [active, setActive] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (slides.length < 2) return;
    timerRef.current = setInterval(
      () => setActive((i) => (i + 1) % slides.length),
      6500,
    );
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  return (
    <section className="relative w-full bg-[#0A0A0A]" data-testid="category-banner">
      <div className="relative h-[42vh] min-h-[320px] md:h-[58vh] md:min-h-[460px] max-h-[640px] w-full overflow-hidden">
        {slides.map((s, i) => {
          const isActive = i === active;
          const hasOverlay = s.overlay_mode !== "image_only" && (s.kicker || s.title || s.copy || s.cta);
          return (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-1000 ease-out ${isActive ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              data-testid={`category-banner-slide-${i}`}
              aria-hidden={!isActive}
            >
              {s.image && (
                <img
                  src={s.image}
                  alt=""
                  className={`absolute inset-0 h-full w-full will-change-transform ${isActive ? "banner-kenburns" : ""}`}
                  style={{ objectFit: s.image_fit, objectPosition: s.image_position }}
                />
              )}

              {hasOverlay && (
                <>
                  {/* Editorial gradient — dark at the bottom for legibility,
                      transparent at the top so the photo breathes. */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent pointer-events-none" />
                  {/* Left-side scrim on desktop for hero-copy legibility */}
                  <div className="hidden md:block absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-black/60 via-black/20 to-transparent pointer-events-none" />

                  <div className="absolute inset-0 flex flex-col justify-end md:justify-center px-6 md:px-16 pb-10 md:pb-0">
                    <div className={`max-w-xl ${isActive ? "banner-reveal" : "opacity-0"}`}>
                      {s.kicker && (
                        <div className="flex items-center gap-3 mb-4">
                          <span className="block h-px w-8 bg-[#E31837]" />
                          <p className="label-caps text-[#E31837]">{s.kicker}</p>
                        </div>
                      )}
                      {s.title && (
                        <h2 className="text-white font-head font-bold uppercase leading-[0.95] tracking-tight text-4xl md:text-6xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]">
                          {s.title}
                        </h2>
                      )}
                      {s.copy && (
                        <p className="text-white/85 mt-4 md:mt-5 text-sm md:text-base max-w-md drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                          {s.copy}
                        </p>
                      )}
                      {s.cta && (
                        <Link
                          to={s.href || shopHref}
                          className="mt-6 md:mt-7 group inline-flex items-center gap-3 bg-white text-[#0A0A0A] pl-6 pr-2 py-2 text-xs md:text-sm font-semibold uppercase tracking-[0.18em] hover:bg-[#E31837] hover:text-white btn-transition"
                          data-testid={`category-banner-cta-${i}`}
                        >
                          {s.cta}
                          <span className="inline-flex h-8 w-8 md:h-9 md:w-9 items-center justify-center bg-[#0A0A0A] text-white group-hover:bg-white group-hover:text-[#E31837] btn-transition">
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        </Link>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}

        {slides.length > 1 && (
          <div className="absolute bottom-5 md:bottom-8 right-6 md:right-16 flex items-center gap-3 z-20">
            {/* slide counter — editorial detail */}
            <span className="hidden md:inline text-white/70 text-xs tracking-[0.2em] font-semibold">
              {String(active + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
            </span>
            <div className="flex items-center gap-1.5">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  data-testid={`category-banner-dot-${i}`}
                  className="group relative h-4 w-10 flex items-center"
                >
                  <span className="block h-[2px] w-full bg-white/25" />
                  <span
                    className={`absolute inset-y-0 left-0 my-auto h-[2px] bg-[#E31837] btn-transition ${i === active ? "w-full" : "w-0 group-hover:w-1/3"}`}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Featured-products rail (desktop only). Anchored to the banner —
            sits above slide transitions so it doesn't flicker on slide change.
            Frosted-glass cards, tight typography, clear CTA affordance. */}
        {featuredProducts.length > 0 && (
          <div
            className="hidden lg:flex absolute top-1/2 -translate-y-1/2 right-8 xl:right-14 z-10 flex-col gap-3 w-[320px] xl:w-[360px]"
            data-testid="category-banner-featured-rail"
          >
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <span className="block h-px w-6 bg-white/50" />
                <p className="label-caps text-white/85">Featured</p>
              </div>
              <Link
                to={shopHref}
                className="label-caps text-white/60 hover:text-white btn-transition flex items-center gap-1"
                data-testid="category-banner-featured-viewall"
              >
                View All <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            {featuredProducts.map((p) => {
              const to = p.group === "equipment"
                ? `/equipment/product/${p.slug}`
                : `/product/${p.slug}`;
              return (
                <Link
                  key={p.id || p.slug}
                  to={to}
                  className="group flex items-center gap-3 bg-white/[0.08] hover:bg-white/[0.14] backdrop-blur-md border border-white/10 hover:border-white/25 p-2 pr-3 btn-transition"
                  data-testid={`category-banner-featured-${p.slug}`}
                >
                  <div className="relative h-16 w-16 shrink-0 overflow-hidden bg-[#0A0A0A]">
                    {p.image && (
                      <img
                        src={p.image}
                        alt={p.name}
                        className="h-full w-full knockout-white"
                        style={{ objectFit: p.image_fit || "cover", objectPosition: p.image_position || "center" }}
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    {p.tag && (
                      <p className="label-caps text-[10px] text-[#E31837] mb-0.5">{p.tag}</p>
                    )}
                    <p className="font-head text-sm font-bold uppercase tracking-tight leading-tight text-white truncate">{p.name}</p>
                    <p className="text-white/80 text-xs mt-0.5">{formatINR(p.price)}</p>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-white/60 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
